import React, { useEffect, useState } from 'react';
import { SCREENS, factoryRightKey, FACTORY_RIGHTS_GROUP } from '../rights';
import { api } from '../App';
import { EyeIcon, EyeOffIcon } from './Dashboard';

const accent = 'var(--gold)';

// Copy-to-clipboard SVG icon (Feather "copy")
function CopyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
    </svg>
  );
}
// Key icon for the "reveal password" affordance on user cards (Feather "key")
function KeyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
    </svg>
  );
}

function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: '2-digit',
    hour: '2-digit', minute: '2-digit'
  });
}

function actionColor(a) {
  if (!a) return 'var(--text3)';
  if (a.startsWith('login'))    return 'var(--teal)';
  if (a.startsWith('task'))     return 'var(--yellow)';
  if (a.startsWith('stage'))    return 'var(--green)';
  if (a.startsWith('order'))    return 'var(--red)';
  return accent;
}

// Short human label for the action chip
function actionLabel(a) {
  return {
    'login':                'Logged in',
    'logout':               'Logged out',
    'task.create':          'Created task',
    'task.comment':         'Commented',
    'task.forward':         'Forwarded task',
    'task.status':          'Changed task status',
    'stage.update':         'Updated stage',
    'stage.skip':           'Skipped stage',
    'order.status':         'Changed order status',
    'order.closure-date':   'Set closure date',
    'order.preorder-stage': 'Updated pre-order stage',
    'order.view':           'Opened order',
    'order.search':         'Searched',
    'page.view':            'Visited page',
    'buyer.drilldown':      'Drilled into buyer',
    'chatbot.open':         'Opened ChatBot',
    'password.change':      'Changed password',
    'password.reveal':      'Viewed password',
    'rights.update':        'Updated user rights',
  }[a] || a;
}

// Build a one-line human description of WHAT the user did, using the
// entity + details JSON the backend captured.
function actionDescription(r) {
  let d = {};
  try { d = r.details ? JSON.parse(r.details) : {}; } catch {}
  switch (r.action) {
    case 'login':
      return d.kind === 'dev' ? 'dev login' : 'phone login';
    case 'task.create':
      return `"${d.title || '(untitled)'}"  ·  ${d.priority || 'Medium'} priority  ·  ${d.assigneeCount || 0} assignee(s)` +
             (d.linked_tas ? `  ·  linked to ${d.linked_tas}` : '');
    case 'task.comment':
      return `task #${r.entity_id}: "${(d.comment || '').slice(0, 80)}"`;
    case 'task.forward':
      return `task #${r.entity_id} → ${(d.added || []).join(', ')}`;
    case 'task.status':
      return `task #${r.entity_id} → ${d.newStatus || '?'}`;
    case 'stage.update':
      return `stage #${r.entity_id}  ·  actual ${d.actual_date || '?'}  ·  ${d.daysDelay > 0 ? `${d.daysDelay}d late` : d.daysDelay < 0 ? `${-d.daysDelay}d early` : 'on time'}  ·  +${d.xpEarned || 0} XP` +
             (d.remark ? `  ·  remark: "${d.remark}"` : '');
    case 'stage.skip':
      return `stage #${r.entity_id} marked not applicable`;
    case 'order.status':
      return `${r.entity_id} → ${d.status || '?'} (${d.rowsUpdated || 0} rows)`;
    case 'order.closure-date':
      return `${r.entity_id} → closure date ${d.closureDate}`;
    case 'order.preorder-stage':
      return `${d.tasno || r.entity_id}  ·  "${d.processStage || '?'}"  ·  ${d.actualDate ? 'set to ' + d.actualDate : 'cleared'}`;
    case 'logout':
      return 'session ended';
    case 'order.view':
      return `${r.entity_id || '?'}` + (d.buyer ? `  ·  buyer ${d.buyer}` : '') + (d.factory ? `  ·  factory ${d.factory}` : '');
    case 'order.search':
      return `query "${d.query || ''}"  →  picked ${d.picked || '?'}` + (d.buyer ? `  (${d.buyer})` : '');
    case 'page.view':
      return `${(r.entity_id || '').replace(/-/g, ' ').toUpperCase()}`;
    case 'buyer.drilldown':
      return `${d.buyer || r.entity_id}  ·  ${d.type === 'fabric' ? 'GSP' : 'TAS'}`;
    case 'chatbot.open':
      return 'opened the assistant';
    case 'password.change':
      return 'updated their account password';
    case 'password.reveal':
      return `viewed password of ${d.target_user || 'user'}`;
    case 'rights.update':
      return `user #${r.entity_id} · ${d.count ?? 0} rights granted`;
    default:
      return r.entity_type ? `${r.entity_type}${r.entity_id ? ' #' + r.entity_id : ''}` : '';
  }
}

export default function Admin({ user }) {
  // Error log is restricted to management (Ritesh, Deepali). Non-management
  // admins (Raj, Grishma) only see the Activity tab. Backend enforces the
  // same restriction — this hide is a UX nicety, not a security boundary.
  //
  // Name fallback: a user who logged in BEFORE the isManagement/isAdmin flags
  // were added to the JWT will have a stale localStorage `dj_user` JSON
  // missing those fields. Without this fallback, even Ritesh would not see
  // the Error Log tab until he logged out + logged back in. Backend will
  // still verify against the live token's isManagement claim.
  const canSeeErrors = !!user && (
    user.isManagement === true ||
    ['Ritesh', 'Deepali'].includes((user.name || '').trim())
  );

  const [tab, setTab] = useState('activity'); // 'activity' | 'errors'
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api('/admin/stats').then(d => setStats(d || null)).catch(() => {});
  }, []);

  // Defensive: if a stale token somehow lands a non-management user on the
  // errors tab, snap them back to activity.
  useEffect(() => {
    if (tab === 'errors' && !canSeeErrors) setTab('activity');
  }, [tab, canSeeErrors]);

  const tabs = [
    { key: 'activity', label: 'ACTIVITY LOG' },
    ...(canSeeErrors ? [{ key: 'developer', label: 'DEVELOPER' }] : []),
    ...(canSeeErrors ? [{ key: 'errors',    label: 'ERROR LOG' }] : []),
    ...(canSeeErrors ? [{ key: 'users',     label: 'USERS' }] : []),
    ...(canSeeErrors ? [{ key: 'rights',    label: 'USER RIGHTS' }] : []),
  ];

  return (
    <div className="screen">
      <div className="header">
        <div>
          <div className="header-logo">RISHAB</div>
          <div className="header-sub">Admin · Audit Logs</div>
        </div>
      </div>

      {/* Stats strip — error counts hidden for non-management to avoid teasing
          info they can't drill into. */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, padding: '16px' }}>
          <StatTile label="Activity (today)" value={stats.activity_today} />
          <StatTile label="Activity (7d)"    value={stats.activity_week} />
          {canSeeErrors && <StatTile label="Errors (today)" value={stats.errors_today} tone="var(--red)" />}
          {canSeeErrors && <StatTile label="Errors (7d)"    value={stats.errors_week}  tone="var(--red)" />}
          <StatTile label="Users (today)"    value={stats.unique_users_today} tone="var(--teal)" />
        </div>
      )}

      {/* Tab switcher */}
      <div style={{ display: 'flex', gap: 8, padding: '4px 16px 14px' }}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '8px 16px', borderRadius: 20, fontSize: 11, letterSpacing: 1.5, fontWeight: 600,
              background: tab === t.key ? accent : 'var(--bg2)',
              border: 'none',
              color:  tab === t.key ? '#FFFFFF' : 'var(--text2)',
              cursor: 'pointer', transition: 'all 0.15s'
            }}>{t.label}</button>
        ))}
      </div>

      {tab === 'activity'  && <ActivityTab canRevealPasswords={canSeeErrors} scope="business" />}
      {tab === 'developer' && canSeeErrors && <ActivityTab canRevealPasswords={canSeeErrors} scope="dev" />}
      {tab === 'errors'    && canSeeErrors && <ErrorsTab />}
      {tab === 'users'     && canSeeErrors && <UsersTab />}
      {tab === 'rights'    && canSeeErrors && <UserRightsTab />}
    </div>
  );
}

function StatTile({ label, value, tone = accent }) {
  return (
    <div style={{ border: 'none', borderRadius: 14, padding: '14px 14px', background: 'var(--bg2)' }}>
      <div style={{ fontSize: 10, color: 'var(--text3)', letterSpacing: 1.5, fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 24, color: tone, fontWeight: 700, marginTop: 6, letterSpacing: '-0.01em' }}>{value ?? 0}</div>
    </div>
  );
}

// ─────────────── Activity tab ───────────────
// scope = 'business' (default) → hides developer users
// scope = 'dev'      → shows ONLY developer users
function ActivityTab({ canRevealPasswords, scope = 'business' }) {
  const [rows, setRows]   = useState([]);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ user: '', action: '', from: '', to: '' });

  function load() {
    setLoading(true);
    const qs = new URLSearchParams();
    qs.set('scope', scope);          // 'business' | 'dev'
    if (filter.user)   qs.set('user', filter.user);
    if (filter.action) qs.set('action', filter.action);
    if (filter.from)   qs.set('from', filter.from);
    if (filter.to)     qs.set('to', filter.to);
    api('/admin/activity?' + qs.toString()).then(d => {
      setRows(d?.rows || []);
      setActions(d?.actions || []);
      setUsers(d?.users || []);
      setLoading(false);
    });
  }
  // Reset user filter when scope changes (a developer-only user_id makes no
  // sense in business scope and vice-versa)
  useEffect(() => { setFilter(f => ({ ...f, user: '' })); }, [scope]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, scope]);

  return (
    <div style={{ padding: '12px 16px' }}>
      <FilterBar
        users={users}
        actions={actions}
        filter={filter}
        setFilter={setFilter}
      />

      {loading && <div style={{ color: 'var(--text3)', padding: 20, textAlign: 'center' }}>Loading…</div>}

      {/* When a single user IS selected → drill into their timeline */}
      {!loading && filter.user ? (
        rows.length === 0 ? (
          <div style={{ color: 'var(--text3)', padding: 20, textAlign: 'center' }}>No activity for this user in the selected date range.</div>
        ) : (
          <UserTimeline rows={rows} userName={(users.find(u => String(u.user_id) === String(filter.user)) || {}).user_name} />
        )
      ) : (
        // Default landing → USER LIST. Click a user → drill into their timeline.
        !loading && (
          <UserList
            users={users}
            onPick={uid => setFilter(f => ({ ...f, user: String(uid) }))}
            canRevealPasswords={canRevealPasswords}
          />
        )
      )}
    </div>
  );
}

// ─────────────── User list (default landing) ───────────────
// Shows everyone who has activity in the current date window, sorted by
// "most recently active". Click a user to see their per-day timeline.
function UserList({ users, onPick, canRevealPasswords }) {
  const [reveal, setReveal] = useState(null); // {id, name} or null
  if (!users || users.length === 0) {
    return <div style={{ color: 'var(--text3)', padding: 20, textAlign: 'center' }}>No users with activity in the selected date range.</div>;
  }
  function relTime(iso) {
    if (!iso) return '—';
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1)    return 'just now';
    if (min < 60)   return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24)    return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 7)    return `${day}d ago`;
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  }
  return (
    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 10, letterSpacing: 2, color: 'var(--text3)', textTransform: 'uppercase', padding: '0 4px 4px' }}>
        Users with activity · {users.length}
      </div>
      {users.map(u => (
        <div
          key={u.user_id}
          style={{
            background: 'var(--bg2)', borderRadius: 12,
            padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12,
            transition: 'background 0.15s'
          }}
        >
          {/* Avatar */}
          <div style={{
            width: 38, height: 38, borderRadius: '50%',
            background: 'var(--gold-bg)', color: 'var(--gold)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 15, flexShrink: 0
          }}>{(u.user_name || '?').trim().charAt(0).toUpperCase()}</div>

          {/* Name + last seen — click to drill into timeline */}
          <button
            onClick={() => onPick(u.user_id)}
            style={{
              flex: 1, minWidth: 0, background: 'none', border: 'none',
              padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit'
            }}
          >
            <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600 }}>{u.user_name || '—'}</div>
            <div style={{ color: 'var(--text3)', fontSize: 11, marginTop: 2 }}>
              last seen {relTime(u.last_activity_at)}
              {u.login_count != null && u.login_count > 0 && (
                <span> · {u.login_count} login{u.login_count === 1 ? '' : 's'}</span>
              )}
            </div>
          </button>

          {/* Activity count */}
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: 'var(--gold)', fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{u.activity_count != null ? u.activity_count : '—'}</div>
            <div style={{ color: 'var(--text3)', fontSize: 9, letterSpacing: 1, textTransform: 'uppercase' }}>activities</div>
          </div>

          {/* Reveal password — management only */}
          {canRevealPasswords && (
            <button
              onClick={e => { e.stopPropagation(); setReveal({ id: u.user_id, name: u.user_name }); }}
              title="Reveal password"
              style={{
                background: 'var(--bg3)', border: 'none', borderRadius: 8,
                width: 32, height: 32, cursor: 'pointer',
                color: 'var(--text2)', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'color 0.15s, background 0.15s'
              }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--gold)'; e.currentTarget.style.background = 'var(--gold-bg)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text2)'; e.currentTarget.style.background = 'var(--bg3)'; }}
            ><KeyIcon /></button>
          )}

          {/* Drill-into chevron */}
          <button
            onClick={() => onPick(u.user_id)}
            style={{
              background: 'none', border: 'none', color: 'var(--text3)',
              fontSize: 16, cursor: 'pointer', padding: '0 4px', flexShrink: 0,
              fontFamily: 'inherit'
            }}
          >›</button>
        </div>
      ))}

      {reveal && <PasswordRevealModal userId={reveal.id} userName={reveal.name} onClose={() => setReveal(null)} />}
    </div>
  );
}

// ─────────────── Password reveal modal (management only) ───────────────
function PasswordRevealModal({ userId, userName, onClose }) {
  const [data, setData]   = useState(null);
  const [err, setErr]     = useState('');
  const [loading, setLoading] = useState(true);
  const [show, setShow]   = useState(false);

  useEffect(() => {
    api(`/admin/users/${userId}/password`).then(d => {
      if (d?.error) { setErr(d.error); setLoading(false); return; }
      setData(d);
      setLoading(false);
    }).catch(() => { setErr('Network error'); setLoading(false); });
  }, [userId]);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      backdropFilter: 'blur(2px)', zIndex: 400,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg2)', borderRadius: 16, padding: '22px 22px 18px',
        width: '100%', maxWidth: 360, boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--gold)', marginBottom: 4 }}>
          Password for {userName}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 16 }}>
          This view has been logged in the audit trail.
        </div>

        {loading && <div style={{ color: 'var(--text3)', padding: 14, textAlign: 'center', fontSize: 12 }}>Fetching…</div>}

        {err && (
          <div style={{
            background: 'var(--red-bg)', color: 'var(--red)',
            padding: 10, borderRadius: 6, fontSize: 12, marginBottom: 12
          }}>{err}</div>
        )}

        {data && !data.known && (
          <div style={{
            background: 'var(--yellow-bg)', color: 'var(--yellow)',
            padding: 12, borderRadius: 8, fontSize: 12, marginBottom: 12, textAlign: 'center'
          }}>
            ⚠ Password not yet captured.<br/>
            It will be saved the next time this user logs in.
          </div>
        )}

        {data && data.known && (
          <div style={{
            background: 'var(--bg)', border: '1px solid var(--border2)',
            padding: '14px 16px', borderRadius: 8, marginBottom: 12,
            display: 'flex', alignItems: 'center', gap: 10
          }}>
            <span style={{ fontSize: 10, letterSpacing: 1.5, color: 'var(--text3)', textTransform: 'uppercase', minWidth: 70 }}>Password</span>
            <span style={{
              flex: 1, color: 'var(--gold)', fontSize: 16, fontWeight: 700,
              fontFamily: 'monospace', letterSpacing: 0.5
            }}>{show ? data.password : '•'.repeat(Math.max(6, (data.password || '').length))}</span>
            <button
              onClick={() => setShow(s => !s)}
              title={show ? 'Hide password' : 'Show password'}
              style={{
                background: 'none', border: 'none', cursor: 'pointer', lineHeight: 0,
                color: show ? 'var(--gold)' : 'var(--text3)', padding: '4px 6px',
                transition: 'color 0.15s'
              }}>{show ? <EyeOffIcon /> : <EyeIcon />}</button>
            <button
              onClick={() => navigator.clipboard?.writeText(data.password)}
              title="Copy to clipboard"
              style={{
                background: 'none', border: 'none', cursor: 'pointer', lineHeight: 0,
                color: 'var(--text3)', padding: '4px 6px',
                transition: 'color 0.15s'
              }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--gold)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}
            ><CopyIcon /></button>
          </div>
        )}

        <button
          onClick={onClose}
          style={{
            width: '100%', background: 'transparent', border: '1px solid var(--border2)',
            color: 'var(--text2)', padding: '10px', borderRadius: 8, cursor: 'pointer',
            fontSize: 13, fontFamily: 'inherit'
          }}>Close</button>
      </div>
    </div>
  );
}

// ─────────────── User timeline (single-user drill-down) ───────────────
// Rows are already in DESC time order from the API. Group by date so it
// reads like a per-day log of what that user did.
function UserTimeline({ rows, userName }) {
  // Group rows by YYYY-MM-DD of created_at
  const groups = {};
  for (const r of rows) {
    const d = new Date(r.created_at);
    const key = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', weekday: 'short' });
    if (!groups[key]) groups[key] = [];
    groups[key].push(r);
  }
  const dayOrder = Object.keys(groups); // preserves insertion order (rows already DESC)

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{
        fontSize: 13, color: 'var(--text2)', marginBottom: 12,
        padding: '8px 14px', background: 'var(--bg2)', borderRadius: 8,
        borderLeft: '3px solid var(--gold)'
      }}>
        <span style={{ color: 'var(--gold)', fontWeight: 600 }}>{userName || 'User'}</span>
        <span style={{ color: 'var(--text3)' }}> · {rows.length} activities</span>
      </div>

      {dayOrder.map(day => (
        <div key={day} style={{ marginBottom: 20 }}>
          {/* Day header */}
          <div style={{
            fontSize: 11, letterSpacing: 1.5, color: 'var(--text3)',
            textTransform: 'uppercase', padding: '0 4px 8px',
            borderBottom: '1px solid var(--border)', marginBottom: 8
          }}>{day} · {groups[day].length} actions</div>

          {/* Timeline rows for this day */}
          {groups[day].map(r => (
            <div key={r.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              padding: '8px 4px'
            }}>
              {/* Time on the left */}
              <span style={{
                color: 'var(--text3)', fontSize: 11, fontWeight: 500,
                minWidth: 60, fontVariantNumeric: 'tabular-nums', flexShrink: 0,
                paddingTop: 1
              }}>
                {new Date(r.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).toLowerCase()}
              </span>
              {/* Dot */}
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: actionColor(r.action), marginTop: 6, flexShrink: 0
              }}/>
              {/* Action + description */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{
                  color: actionColor(r.action), fontWeight: 700, fontSize: 11,
                  letterSpacing: 0.3, textTransform: 'uppercase', marginRight: 8
                }}>{actionLabel(r.action)}</span>
                {actionDescription(r) && (
                  <span style={{ fontSize: 12, color: 'var(--text2)' }}>{actionDescription(r)}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ─────────────── Errors tab ───────────────
function ErrorsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ path: '', from: '', to: '' });
  const [expanded, setExpanded] = useState(null);

  function load() {
    setLoading(true);
    const qs = new URLSearchParams();
    if (filter.path) qs.set('path', filter.path);
    if (filter.from) qs.set('from', filter.from);
    if (filter.to)   qs.set('to',   filter.to);
    api('/admin/errors?' + qs.toString()).then(d => {
      setRows(d?.rows || []);
      setLoading(false);
    });
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div style={{ padding: '12px 16px' }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <input
          placeholder="Filter by path…"
          value={filter.path}
          onChange={e => setFilter(f => ({ ...f, path: e.target.value }))}
          style={inputStyle}
        />
        <input type="date" value={filter.from} onChange={e => setFilter(f => ({ ...f, from: e.target.value }))} style={inputStyle} />
        <input type="date" value={filter.to}   onChange={e => setFilter(f => ({ ...f, to: e.target.value }))}   style={inputStyle} />
        {(filter.path || filter.from || filter.to) && (
          <button onClick={() => setFilter({ path: '', from: '', to: '' })} style={btnGhost}>Clear</button>
        )}
      </div>

      {loading && <div style={{ color: 'var(--text3)', padding: 20, textAlign: 'center' }}>Loading…</div>}
      {!loading && rows.length === 0 && <div style={{ color: 'var(--text3)', padding: 20, textAlign: 'center' }}>No errors. 🎉</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
      {rows.map(r => (
        <div key={r.id}
          onClick={() => setExpanded(expanded === r.id ? null : r.id)}
          style={{
            background: 'var(--bg2)', borderRadius: 12, padding: '12px 14px',
            cursor: 'pointer', transition: 'background 0.15s'
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg3)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg2)'; }}
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--red)', fontWeight: 700, fontSize: 11, letterSpacing: 0.5 }}>{r.level || 'ERROR'}</span>
            <span style={{ color: 'var(--text)', fontSize: 12, fontWeight: 500 }}>{r.method} {r.path}</span>
            {r.user_name && <span style={{ color: 'var(--text3)', fontSize: 11 }}>{r.user_name}</span>}
            <span style={{ marginLeft: 'auto', color: 'var(--text3)', fontSize: 11, fontWeight: 500 }}>{fmtDateTime(r.created_at)}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6 }}>{r.message}</div>
          {expanded === r.id && (
            <div style={{ marginTop: 6 }}>
              {r.stack && (
                <pre style={{
                  fontSize: 10, color: 'var(--text2)', background: 'var(--bg2)', padding: 8,
                  borderRadius: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                  maxHeight: 280, overflow: 'auto'
                }}>{r.stack}</pre>
              )}
              {r.request_body && (
                <details style={{ marginTop: 6 }}>
                  <summary style={{ fontSize: 11, color: 'var(--text3)', cursor: 'pointer' }}>request body</summary>
                  <pre style={{
                    fontSize: 10, color: 'var(--text2)', background: 'var(--bg2)', padding: 8,
                    borderRadius: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-all'
                  }}>{r.request_body}</pre>
                </details>
              )}
              <div style={{ marginTop: 4, fontSize: 10, color: 'var(--text3)' }}>IP: {r.ip || '—'}</div>
            </div>
          )}
        </div>
      ))}
      </div>
    </div>
  );
}

function FilterBar({ users, actions, filter, setFilter }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <select value={filter.user} onChange={e => setFilter(f => ({ ...f, user: e.target.value }))} style={inputStyle}>
        <option value="">All users</option>
        {users.map(u => <option key={u.user_id} value={u.user_id}>{u.user_name}</option>)}
      </select>
      <select value={filter.action} onChange={e => setFilter(f => ({ ...f, action: e.target.value }))} style={inputStyle}>
        <option value="">All actions</option>
        {actions.map(a => <option key={a} value={a}>{a}</option>)}
      </select>
      <input type="date" value={filter.from} onChange={e => setFilter(f => ({ ...f, from: e.target.value }))} style={inputStyle} />
      <input type="date" value={filter.to}   onChange={e => setFilter(f => ({ ...f, to: e.target.value }))}   style={inputStyle} />
      {(filter.user || filter.action || filter.from || filter.to) && (
        <button onClick={() => setFilter({ user: '', action: '', from: '', to: '' })} style={btnGhost}>Clear</button>
      )}
    </div>
  );
}

const inputStyle = {
  background: 'var(--bg2)', color: 'var(--text)', border: 'none',
  borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 500, outline: 'none'
};
const btnGhost = {
  background: 'var(--bg2)', color: 'var(--text2)', border: 'none',
  borderRadius: 8, padding: '8px 14px', fontSize: 11, cursor: 'pointer', fontWeight: 600
};

// ─────────────── USER RIGHTS tab (dept-head only) ───────────────
// Two-pane: left = user picker, right = checkbox grid grouped by section.
// Management users are shown but rights aren't editable — they implicitly
// have everything.
function UserRightsTab() {
  const [catalog, setCatalog] = useState([]);     // [{key,label,group}]
  const [users,   setUsers]   = useState([]);     // [{id,display_name,is_management,is_admin,right_count}]
  const [selected, setSelected] = useState(null); // user object
  const [granted,  setGranted]  = useState(new Set()); // Set of right_keys
  const [original, setOriginal] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [toast,  setToast]  = useState('');

  useEffect(() => {
    Promise.all([
      api('/admin/rights/catalog'),
      api('/admin/rights/users'),
      api('/admin/factories').catch(() => []),
    ]).then(([cat, usr, facs]) => {
      // Server catalog (tabs, actions, admin) + every screen card from app/src/rights.js.
      // Not shown (never enforced; stored ticks are left untouched):
      //   War Room (dashboard.*) — replaced by the RW / TAS / GSP screen rights
      //   Orders (orders.*)      — not used
      const HIDDEN_GROUPS = ['War Room', 'Orders'];
      // GMT Factory → one tick per factory: only ticked factories show on the Factory screen.
      const facRights = (Array.isArray(facs) ? facs : []).map(f => ({ key: factoryRightKey(f.id), label: f.name, group: FACTORY_RIGHTS_GROUP }));
      setCatalog([...(Array.isArray(cat) ? cat : []).filter(r => !HIDDEN_GROUPS.includes(r.group)), ...SCREENS, ...facRights]);
      setUsers(Array.isArray(usr) ? usr : []);
    });
  }, []);

  function pickUser(u) {
    setSelected(u);
    setSaving(false);
    setToast('');
    if (u.is_management) {
      // Management implicitly has everything; show all-checked, read-only.
      const all = new Set(catalog.map(r => r.key));
      setGranted(all);
      setOriginal(all);
      return;
    }
    api(`/admin/rights/${u.id}`).then(d => {
      const s = new Set(d?.rights || []);
      setGranted(s);
      setOriginal(s);
    });
  }

  function toggle(key) {
    if (selected?.is_management) return; // read-only for mgmt
    setGranted(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function save() {
    if (!selected || selected.is_management) return;
    setSaving(true);
    api(`/admin/rights/${selected.id}`, {
      method: 'POST',
      body: JSON.stringify({ rights: Array.from(granted) })
    }).then(d => {
      setSaving(false);
      if (d?.success) {
        setOriginal(new Set(granted));
        setToast('✓ Rights saved');
        setTimeout(() => setToast(''), 2000);
        // refresh user list to update right_count
        api('/admin/rights/users').then(u => setUsers(Array.isArray(u) ? u : []));
      } else {
        setToast(d?.error || 'Save failed');
      }
    });
  }

  function selectGroup(group, on) {
    if (selected?.is_management) return;
    const keys = catalog.filter(r => r.group === group).map(r => r.key);
    setGranted(prev => {
      const next = new Set(prev);
      keys.forEach(k => on ? next.add(k) : next.delete(k));
      return next;
    });
  }

  // Group catalog for display
  const grouped = {};
  for (const r of catalog) {
    if (!grouped[r.group]) grouped[r.group] = [];
    grouped[r.group].push(r);
  }
  const groupOrder = Object.keys(grouped);

  const hasChanges = granted.size !== original.size ||
    [...granted].some(k => !original.has(k));

  return (
    <div style={{ padding: '12px 16px', display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: 16, alignItems: 'flex-start' }}>
      {/* LEFT — user list */}
      <div>
        <div style={{ fontSize: 10, letterSpacing: 2, color: 'var(--text3)', textTransform: 'uppercase', padding: '0 4px 8px' }}>
          Users · {users.length}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {users.map(u => {
            const isSel = selected?.id === u.id;
            return (
              <button key={u.id} onClick={() => pickUser(u)} style={{
                background: isSel ? 'var(--gold-bg)' : 'var(--bg2)',
                border: isSel ? '1px solid var(--gold)' : '1px solid transparent',
                borderRadius: 10, padding: '10px 12px', cursor: 'pointer',
                textAlign: 'left', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: 10
              }}>
                <div style={{
                  width: 30, height: 30, borderRadius: '50%',
                  background: 'var(--gold-bg)', color: 'var(--gold)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 12, flexShrink: 0
                }}>{(u.display_name || '?').trim().charAt(0).toUpperCase()}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: 'var(--text)', fontSize: 13, fontWeight: 600 }}>{u.display_name}</div>
                  <div style={{ color: 'var(--text3)', fontSize: 10, marginTop: 1 }}>
                    {u.is_management ? 'Management · all rights' : `${u.right_count} right${u.right_count === 1 ? '' : 's'}`}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* RIGHT — rights editor */}
      <div>
        {!selected ? (
          <div style={{
            padding: 40, textAlign: 'center', color: 'var(--text3)', fontSize: 13,
            background: 'var(--bg2)', borderRadius: 12
          }}>Pick a user from the left to view / edit their rights.</div>
        ) : (
          <>
            <div style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              marginBottom: 14, flexWrap: 'wrap', gap: 8
            }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--gold)' }}>
                  {selected.display_name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                  {selected.is_management
                    ? 'Management user — has every right by default (read-only here).'
                    : `${catalog.filter(r => granted.has(r.key)).length} of ${catalog.length} rights granted`}
                </div>
              </div>
              {!selected.is_management && (
                <div style={{ display: 'flex', gap: 6 }}>
                  {hasChanges && (
                    <button onClick={() => setGranted(new Set(original))} style={btnGhost}>Reset</button>
                  )}
                  <button onClick={save} disabled={!hasChanges || saving} style={{
                    background: hasChanges ? 'var(--gold)' : 'var(--bg3)',
                    border: 'none', color: hasChanges ? 'var(--on-gold)' : 'var(--text3)',
                    padding: '8px 18px', borderRadius: 8, cursor: hasChanges ? 'pointer' : 'default',
                    fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
                    opacity: saving ? 0.7 : 1
                  }}>{saving ? 'Saving…' : 'Save Changes'}</button>
                </div>
              )}
            </div>

            {toast && (
              <div style={{
                background: 'var(--green-bg)', color: 'var(--green)',
                padding: '8px 12px', borderRadius: 6, fontSize: 12, marginBottom: 12
              }}>{toast}</div>
            )}

            {groupOrder.map(group => {
              const keys = grouped[group];
              const allOn = keys.every(r => granted.has(r.key));
              const someOn = !allOn && keys.some(r => granted.has(r.key));
              return (
                <div key={group} style={{
                  background: 'var(--bg2)', borderRadius: 12, padding: '14px 16px',
                  marginBottom: 10
                }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    marginBottom: 10
                  }}>
                    <div style={{
                      fontSize: 11, letterSpacing: 1.5, color: 'var(--gold)',
                      fontWeight: 700, textTransform: 'uppercase'
                    }}>{group}</div>
                    {!selected.is_management && (
                      <button
                        onClick={() => selectGroup(group, !allOn)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          fontSize: 10, color: 'var(--text3)', letterSpacing: 1, fontFamily: 'inherit',
                          textTransform: 'uppercase', fontWeight: 600
                        }}>{allOn ? 'Uncheck all' : someOn ? 'Check rest' : 'Check all'}</button>
                    )}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 6 }}>
                    {keys.map(r => {
                      const on = granted.has(r.key);
                      return (
                        <label key={r.key} style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '6px 8px', borderRadius: 6,
                          cursor: selected.is_management ? 'default' : 'pointer',
                          background: on ? 'var(--gold-bg)' : 'transparent',
                          opacity: selected.is_management ? 0.7 : 1
                        }}>
                          <input
                            type="checkbox"
                            checked={on}
                            disabled={selected.is_management}
                            onChange={() => toggle(r.key)}
                            style={{ accentColor: 'var(--gold)', cursor: 'inherit' }}
                          />
                          <span style={{ fontSize: 12, color: on ? 'var(--text)' : 'var(--text2)' }}>
                            {r.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- USERS TAB
// Full user administration: create new users, change role, map a Factory user to
// a mst_party factory, activate/deactivate, reset password. Rights themselves
// still live on the USER RIGHTS tab; role here just picks the default rights
// set at creation and the role-based booleans (is_management/is_admin/is_factory).
function UsersTab() {
  const [users, setUsers]       = useState(null);
  const [factories, setFactories] = useState([]);
  const [showAdd, setShowAdd]   = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [msg, setMsg]           = useState('');

  const load = () => {
    api('/admin/users').then(d => setUsers(Array.isArray(d) ? d : []));
    api('/admin/factories').then(d => setFactories(Array.isArray(d) ? d : []));
  };
  useEffect(load, []);

  const roleOf = u => u.is_management ? 'Management' : u.is_admin ? 'Admin' : u.is_factory ? 'Factory' : 'User';
  const roleColor = r => r === 'Management' ? 'var(--gold)' : r === 'Admin' ? 'var(--teal)' : r === 'Factory' ? '#c07033' : 'var(--text3)';

  const toggleActive = async (u) => {
    if (!window.confirm((u.is_active ? 'Deactivate ' : 'Activate ') + u.display_name + '?')) return;
    try {
      const r = await fetch('/api/admin/users/' + u.id, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('dj_token') },
        body: JSON.stringify({ is_active: !u.is_active }),
      });
      if (!r.ok) throw new Error((await r.json()).error || 'Update failed');
      load();
    } catch (e) { setMsg(e.message); }
  };

  return (
    <div style={{ padding: '0 16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0 14px' }}>
        <div style={{ fontSize: 12, color: 'var(--text3)' }}>{users ? users.length + ' users' : 'Loading…'}</div>
        <button onClick={() => setShowAdd(true)} style={{ background: accent, color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>+ NEW USER</button>
      </div>
      {msg && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 10 }}>{msg}</div>}

      {users === null ? <div className="loading">Loading users…</div> : (
        <div style={{ display: 'grid', gap: 8 }}>
          {users.map(u => {
            const role = roleOf(u);
            return (
              <div key={u.id} className="card" style={{ padding: 12, opacity: u.is_active ? 1 : 0.5 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700 }}>{u.display_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>{u.username}{u.factory_name ? ' · ' + u.factory_name : ''}</div>
                  </div>
                  <div style={{ fontSize: 10, letterSpacing: 1, padding: '3px 10px', borderRadius: 12, border: '1px solid ' + roleColor(role), color: roleColor(role) }}>{role.toUpperCase()}</div>
                  <button onClick={() => setEditUser(u)} style={{ background: 'none', border: '1px solid var(--border2)', color: 'var(--text2)', padding: '4px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>EDIT</button>
                  <button onClick={() => toggleActive(u)} style={{ background: 'none', border: '1px solid ' + (u.is_active ? 'var(--red)' : 'var(--green)'), color: u.is_active ? 'var(--red)' : 'var(--green)', padding: '4px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}>{u.is_active ? 'DEACTIVATE' : 'ACTIVATE'}</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd  && <UserFormModal factories={factories} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editUser && <UserFormModal factories={factories} user={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} />}
    </div>
  );
}

function UserFormModal({ factories, user, onClose, onSaved }) {
  const editing = !!user;
  const [displayName, setDisplayName] = useState(user ? user.display_name : '');
  const [username, setUsername]       = useState(user ? user.username : '');
  const [password, setPassword]       = useState('');
  const [resetPwd, setResetPwd]       = useState('');
  const initialRole = user ? (user.is_management ? 'management' : user.is_admin ? 'admin' : user.is_factory ? 'factory' : 'user') : 'user';
  const [role, setRole] = useState(initialRole);
  const [factoryPartyId, setFactoryPartyId] = useState(user && user.factory_partyid ? user.factory_partyid : '');
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState('');

  const save = async () => {
    setBusy(true); setErr('');
    try {
      if (editing) {
        const body = { display_name: displayName, username, role, factory_partyid: role === 'factory' ? factoryPartyId : null };
        const r = await fetch('/api/admin/users/' + user.id, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('dj_token') }, body: JSON.stringify(body) });
        if (!r.ok) throw new Error((await r.json()).error || 'Save failed');
        if (resetPwd) {
          const rp = await fetch('/api/admin/users/' + user.id + '/password', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('dj_token') }, body: JSON.stringify({ password: resetPwd }) });
          if (!rp.ok) throw new Error((await rp.json()).error || 'Password reset failed');
        }
      } else {
        const body = { display_name: displayName, username, password, role, factory_partyid: role === 'factory' ? factoryPartyId : null };
        const r = await fetch('/api/admin/users', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + localStorage.getItem('dj_token') }, body: JSON.stringify(body) });
        if (!r.ok) throw new Error((await r.json()).error || 'Create failed');
      }
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const fieldStyle = { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border2)', background: 'var(--bg)', color: 'var(--text)', fontSize: 13, boxSizing: 'border-box' };
  const labelStyle = { fontSize: 10, letterSpacing: 1, color: 'var(--text3)', margin: '10px 0 4px', textTransform: 'uppercase' };
  const rBtn = on => ({ padding: '6px 12px', border: '1px solid ' + (on ? accent : 'var(--border2)'), background: on ? accent : 'transparent', color: on ? '#fff' : 'var(--text2)', borderRadius: 6, fontSize: 11, letterSpacing: 0.5, fontWeight: 700, cursor: 'pointer' });

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg2)', color: 'var(--text)', borderRadius: 14, width: '100%', maxWidth: 460, maxHeight: '92vh', overflow: 'auto', padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontWeight: 800, letterSpacing: .5 }}>{editing ? 'EDIT · ' + user.display_name : 'NEW USER'}</div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid var(--border2)', color: 'var(--text3)', width: 26, height: 26, borderRadius: 4, cursor: 'pointer' }}>✕</button>
        </div>
        <div style={labelStyle}>Full name</div>
        <input style={fieldStyle} value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="e.g. Priya Sharma" />
        <div style={labelStyle}>Username / phone</div>
        <input style={fieldStyle} value={username} onChange={e => setUsername(e.target.value)} placeholder="10-digit phone or short handle" />
        {!editing && <>
          <div style={labelStyle}>Password</div>
          <input style={fieldStyle} type="text" value={password} onChange={e => setPassword(e.target.value)} placeholder="min 4 characters" />
        </>}
        <div style={labelStyle}>Role</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {['user', 'admin', 'management', 'factory'].map(r => (
            <button key={r} type="button" onClick={() => setRole(r)} style={rBtn(role === r)}>{r.toUpperCase()}</button>
          ))}
        </div>
        {role === 'factory' && <>
          <div style={labelStyle}>Factory (from mst_party)</div>
          <select style={fieldStyle} value={factoryPartyId} onChange={e => setFactoryPartyId(e.target.value)}>
            <option value="">— pick factory —</option>
            {factories.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Factory users see Daily Production, Fab Inhouse, and Cost Sheet for the orders assigned to this factory (customizable under User Rights → GMT Factory).</div>
        </>}
        {editing && <>
          <div style={labelStyle}>Reset password (leave blank to keep)</div>
          <input style={fieldStyle} type="text" value={resetPwd} onChange={e => setResetPwd(e.target.value)} placeholder="min 4 characters" />
        </>}
        {err && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid var(--border2)', color: 'var(--text2)', padding: '8px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>CANCEL</button>
          <button onClick={save} disabled={busy || !displayName || !username || (!editing && !password)} style={{ background: accent, color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>{busy ? 'SAVING…' : editing ? 'SAVE' : 'CREATE USER'}</button>
        </div>
      </div>
    </div>
  );
}
